import { useCallback, useRef, useState, useEffect } from 'react';
import isFileTypeAccepted from '@/utils/file/isFileTypeAccepted';

interface UseFileUploadOptions {
  accept: string;
  value?: string | null;
  hasUploadError?: boolean;
  onUpload: (file: File) => boolean | Promise<boolean>;
  onRemove?: () => void;
  showInvalidFeedback: () => void;
}

/**
 * Hook to manage file upload logic including local preview and validation
 */
const useFileUpload = ({
  accept,
  value,
  hasUploadError,
  onUpload,
  onRemove,
  showInvalidFeedback,
}: UseFileUploadOptions) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Clear local preview when CDN URL arrives or upload fails
  useEffect(() => {
    if (localPreview) {
      // Clear if CDN URL arrived or upload failed
      if (value || hasUploadError) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
      }
    }
  }, [value, localPreview, hasUploadError]);

  // Clean up blob URL when localPreview changes or on unmount
  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      const accepted = await onUpload(file);
      if (!accepted) {
        return;
      }
      const localUrl = URL.createObjectURL(file);
      setLocalPreview(localUrl);
    },
    [onUpload]
  );

  // Common validation and file processing logic used by both file chooser and drag-and-drop
  const validateAndProcessFile = useCallback(
    (file: File) => {
      if (!isFileTypeAccepted({ fileType: file.type, accept })) {
        // eslint-disable-next-line no-console
        console.warn(
          `[UploadArea] File type "${file.type}" not accepted. Expected: ${accept}`
        );
        showInvalidFeedback();
        return false;
      }
      handleFileSelect(file);
      return true;
    },
    [accept, showInvalidFeedback, handleFileSelect]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        validateAndProcessFile(file);
      }
      // Reset input so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [validateAndProcessFile]
  );

  const handleRemove = useCallback(() => {
    // Start removal animation
    setIsRemoving(true);

    // Wait for animation to complete, then clean up
    setTimeout(() => {
      // Clear local preview
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
      }
      setIsRemoving(false);
      // Call parent's onRemove
      onRemove?.();
    }, 150); // Match animation duration
  }, [localPreview, onRemove]);

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  // Use local preview if available, otherwise use CDN URL
  const previewUrl = localPreview || value;

  return {
    inputRef,
    previewUrl,
    isRemoving,
    handleChange,
    handleRemove,
    openFilePicker,
    validateAndProcessFile,
  };
};

export default useFileUpload;
